// Canonical catalogs for the rulebuilder, mirroring
// raw/rulebuilder/pricing_rule_entity.md. These drive contracts, node panels,
// validation, and serialization across the entity and the rule-builder widget.

/** Node types in the control-flow graph. */
export const NODE_TYPES = [
	'start',
	'condition',
	'switch',
	'set',
	'set_price',
	'alert',
	'interpret',
	'competitor_price',
	'competitor_weighted_price',
] as const

/** Declared-variable value types. */
export const VARIABLE_TYPES = ['string', 'number', 'boolean'] as const

/**
 * Start-node param types. Competitors are picked via `competitor_list`.
 * `price_column` holds a field-schema id — the same `<id>` that addresses the
 * column as `pricing_line.<id>` inside the rule graph.
 */
export const PARAM_TYPES = [
	'string',
	'integer',
	'decimal',
	'boolean',
	'enum',
	'competitor_list',
	'aggregation_type',
	'price_column',
] as const

// --- Boolean (condition) operations, grouped by arity ---

export const COMPARISON_OPS = ['eq', 'ne', 'gt', 'gte', 'lt', 'lte'] as const
export const IDENTITY_OPS = ['is', 'is_not'] as const
export const NULLCHECK_OPS = ['is_null', 'is_not_null'] as const
export const MEMBERSHIP_OPS = ['in', 'not_in'] as const
export const LOGICAL_OPS = ['and', 'or'] as const
export const NEGATION_OPS = ['not'] as const

export const BOOLEAN_OPS = [
	...COMPARISON_OPS,
	...IDENTITY_OPS,
	...NULLCHECK_OPS,
	...MEMBERSHIP_OPS,
	...LOGICAL_OPS,
	...NEGATION_OPS,
] as const

/**
 * Arity per boolean operation. `'n'` denotes variadic (logical and/or).
 * Used by the graph validator to check `condition.expr` shape.
 */
export const BOOLEAN_OP_ARITY: Record<
	(typeof BOOLEAN_OPS)[number],
	number | 'n'
> = {
	eq: 2,
	ne: 2,
	gt: 2,
	gte: 2,
	lt: 2,
	lte: 2,
	is: 2,
	is_not: 2,
	is_null: 1,
	is_not_null: 1,
	in: 2,
	not_in: 2,
	and: 'n',
	or: 'n',
	not: 1,
}

// --- Arithmetic (set / set_price) operations ---

export const ARITHMETIC_BINARY_OPS = ['+', '-', '*', '/'] as const
export const ARITHMETIC_UNARY_OPS = ['abs', 'neg'] as const

/**
 * Aggregations the `competitor_price` node accepts, and so the catalog an
 * `aggregation_type` param offers. Competitors are an unordered set of prices,
 * which is why `sum` and `first` are absent — the engine rejects them.
 */
export const COMPETITOR_AGGREGATIONS = [
	'min',
	'max',
	'avg',
	'mode',
	'median',
	'mode_second_unique',
] as const

/**
 * Aggregations the `agg` arithmetic operation accepts. Its values are an ordered,
 * author-written list, so `sum` and `first` are meaningful here. `mode_second_unique`
 * is absent — it is a competitor-price heuristic, not a general fold.
 *
 * An `agg` aggregation is always a literal chosen in the expression, never a param,
 * so this list is never offered as a param type.
 */
export const ARITHMETIC_AGGREGATIONS = [
	'min',
	'max',
	'avg',
	'mode',
	'median',
	'sum',
	'first',
] as const

export type Aggregation =
	| (typeof COMPETITOR_AGGREGATIONS)[number]
	| (typeof ARITHMETIC_AGGREGATIONS)[number]

/**
 * Human labels for both aggregation catalogs, used as the `defaultValue` for the
 * `t(<aggregation>)` lookup so a value without a translation key still reads
 * properly instead of rendering its raw snake_case identifier.
 */
export const AGGREGATION_LABELS: Record<Aggregation, string> = {
	min: 'Min',
	max: 'Max',
	avg: 'Avg',
	mode: 'Mode',
	median: 'Median',
	sum: 'Sum',
	first: 'First',
	mode_second_unique: 'Mode (2nd cheapest)',
}

/** `ref` namespace prefixes available inside expressions. */
export const REF_NAMESPACES = ['params', 'vars', 'pricing_line'] as const

/**
 * Static alerts catalog (SPEC decision #3). `alert.alert` must come from a
 * DB-managed catalog; until that endpoint exists, this static catalog is the
 * source of truth — a query hook can replace it when the backend lands.
 * Codes are grounded in the documented reference client rules; do not invent
 * new ones here. `labelKey` is a translation key (`rb_alert_<code>`).
 */
export const ALERTS_CATALOG = [
	{ code: 'no_cd', labelKey: 'rb_alert_no_cd' },
	{ code: 'no_competitor_found', labelKey: 'rb_alert_no_competitor_found' },
	{ code: 'no_cost', labelKey: 'rb_alert_no_cost' },
	{ code: 'no_plan_markup', labelKey: 'rb_alert_no_plan_markup' },
	{ code: 'no_rank', labelKey: 'rb_alert_no_rank' },
	{ code: 'no_target_margin', labelKey: 'rb_alert_no_target_margin' },
	{
		code: 'reached_maximum_decrease',
		labelKey: 'rb_alert_reached_maximum_decrease',
	},
] as const

/** Alert codes derived from {@link ALERTS_CATALOG}, for schema/validation use. */
export const ALERT_CODES = ALERTS_CATALOG.map((a) => a.code) as [
	(typeof ALERTS_CATALOG)[number]['code'],
	...(typeof ALERTS_CATALOG)[number]['code'][],
]

/** PricingRule lifecycle status. */
export const RULE_STATUSES = ['draft', 'published'] as const

/** Empty rule envelope used when authoring a brand-new draft. */
export const EMPTY_RULE = {
	variables: [],
	logic: { nodes: [], edges: [] },
} as const
